import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

// Details remembered on the phone so trainees don't retype them; attendance
// already stores name and email, which are used when nothing else is saved.
const IDENTITY_KEY = 'lp_feedback_identity';
const ATTENDANCE_KEY = 'lp_attendance_identity';
const DEVICE_KEY = 'lp_device_id';

const readJson = (key) => {
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
};

// A random id kept on this phone, used to stop repeat anonymous responses.
const deviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

const OTHER = '__other__';

const page = {
  minHeight: '100vh', background: '#f3f4f6', display: 'flex',
  alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', boxSizing: 'border-box'
};
const card = { width: '100%', maxWidth: '640px', padding: '20px' };

// 1-5 buttons (plus N/A where allowed) with the scale's end labels underneath.
const Scale = ({ value, onChange, labels, na, invalid }) => (
  <div>
    <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)} className="scale-btn"
          style={value === n ? { background: '#f97316', color: 'white', borderColor: '#f97316' } : invalid ? { borderColor: '#ef4444' } : undefined}>
          {n}
        </button>
      ))}
      {na && (
        <button type="button" onClick={() => onChange('NA')} className="scale-btn" style={{
          width: 'auto', padding: '0 12px',
          ...(value === 'NA' ? { background: '#6b7280', color: 'white', borderColor: '#6b7280' } : invalid ? { borderColor: '#ef4444' } : {})
        }}>N/A</button>
      )}
    </div>
    <div className="flex flex-between text-muted" style={{ fontSize: '11px', marginTop: '4px', maxWidth: '300px' }}>
      <span>1 = {labels.low}</span><span>5 = {labels.high}</span>
    </div>
  </div>
);

// Free text, or a dropdown with "Other" that reveals a text box.
const ListOrText = ({ label, options, value, onChange, required, invalid }) => {
  const inList = options && options.includes(value);
  const [other, setOther] = useState(options && value && !inList);
  return (
    <div className="form-group">
      <label>{label}{required && ' *'}</label>
      {options ? (
        <>
          <select value={other ? OTHER : (inList ? value : '')} style={invalid ? { borderColor: '#ef4444' } : undefined}
            onChange={e => {
              if (e.target.value === OTHER) { setOther(true); onChange(''); }
              else { setOther(false); onChange(e.target.value); }
            }}>
            <option value="">— Select —</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
            <option value={OTHER}>Other…</option>
          </select>
          {other && (
            <input type="text" className="mt-2" maxLength={255} placeholder="Please specify" value={value}
              onChange={e => onChange(e.target.value)} />
          )}
        </>
      ) : (
        <input type="text" maxLength={255} value={value} style={invalid ? { borderColor: '#ef4444' } : undefined}
          onChange={e => onChange(e.target.value)} />
      )}
    </div>
  );
};

const PublicFeedback = () => {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [identity, setIdentity] = useState(() => {
    const saved = readJson(IDENTITY_KEY);
    const attendance = readJson(ATTENDANCE_KEY);
    return {
      name: saved.name || attendance.name || '',
      email: saved.email || attendance.email || '',
      site: saved.site || '',
      function: saved.function || ''
    };
  });
  const [answers, setAnswers] = useState({});
  const [trainerRatings, setTrainerRatings] = useState({});
  const [trainerComments, setTrainerComments] = useState({});
  const [missing, setMissing] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/feedback/public/${token}`)
      .then(({ data }) => setForm(data))
      .catch(err => setLoadError(err.response?.data?.error || 'Could not load this feedback form. Check your connection and try again.'));
  }, [token]);

  if (loadError) {
    return <div style={page}><div className="card" style={card}><div className="alert alert-error">{loadError}</div></div></div>;
  }
  if (!form) return <div style={page}><div className="text-center mt-5">Loading...</div></div>;

  const named = form.identity_mode === 'named';
  const anonymous = form.identity_mode === 'anonymous';
  const items = form.template.sections.flatMap(s => s.items);

  const header = (
    <div className="text-center mb-4">
      <img src="/lp-mark.png" alt="" style={{ height: '40px' }} />
      <h2 style={{ margin: '8px 0 4px' }}>{form.title}</h2>
      {form.training_title && <p className="text-muted text-sm">{form.training_title}</p>}
    </div>
  );

  if (done) {
    return (
      <div style={page}>
        <div className="card text-center" style={card}>
          {header}
          <div style={{ fontSize: '56px', color: '#10b981', lineHeight: 1 }}>✓</div>
          <h2 style={{ margin: '12px 0 4px' }}>Thank you for your feedback</h2>
          <p className="text-muted">{done.updated ? 'Your earlier response has been updated.' : 'Your response has been recorded.'}</p>
          <p className="text-muted text-sm mt-3">You can close this page.</p>
        </div>
      </div>
    );
  }

  if (form.status !== 'open') {
    return (
      <div style={page}>
        <div className="card" style={card}>
          {header}
          <div className="alert alert-info">
            {form.status === 'not_started' ? 'This feedback form is not open yet.' : 'This feedback form has closed. Thank you.'}
          </div>
        </div>
      </div>
    );
  }

  const setAnswer = (key, value) => {
    setAnswers(a => ({ ...a, [key]: value }));
    setMissing(m => { const n = new Set(m); n.delete(key); return n; });
  };
  const setIdent = (key, value) => {
    setIdentity(i => ({ ...i, [key]: value }));
    setMissing(m => { const n = new Set(m); n.delete(key); return n; });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    // Same checks as the server, so trainees see what is missing straight away.
    const gaps = [];
    if (named) {
      ['name', 'email', 'site', 'function'].forEach(k => { if (!identity[k].trim()) gaps.push(k); });
    }
    items.forEach(i => {
      const v = answers[i.key];
      if (i.required && (v === undefined || (i.type === 'text' && !String(v).trim()))) gaps.push(i.key);
      if (i.followUp && i.followUp.when.includes(v) && !i.followUp.options.includes(answers[i.followUp.key])) gaps.push(i.followUp.key);
    });
    form.trainer_names.forEach(n => { if (trainerRatings[n] === undefined) gaps.push(`trainer:${n}`); });
    if (gaps.length) {
      setMissing(new Set(gaps));
      setError(`Please answer the ${gaps.length} highlighted question${gaps.length === 1 ? '' : 's'}.`);
      setTimeout(() => document.querySelector('[data-missing="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post(`/feedback/public/${token}`, {
        ...(anonymous ? {} : { name: identity.name, email: identity.email }),
        site: identity.site,
        function: identity.function,
        answers,
        trainer_ratings: trainerRatings,
        trainer_comments: trainerComments,
        device_id: deviceId()
      });
      try { localStorage.setItem(IDENTITY_KEY, JSON.stringify(anonymous ? { site: identity.site, function: identity.function } : identity)); } catch { /* private mode */ }
      setDone(data);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send your feedback. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const block = (key, children, alsoKey) => {
    const isMissing = missing.has(key) || (alsoKey && missing.has(alsoKey));
    return (
      <div key={key} data-missing={isMissing ? 'true' : undefined}
        className="feedback-item" style={isMissing ? { background: '#fef2f2' } : undefined}>
        {children}
      </div>
    );
  };

  return (
    <div style={page}>
      <div className="card" style={card}>
        {header}
        <p className="text-sm" style={{ marginBottom: '8px' }}>{form.template.intro}</p>
        <p className="text-muted text-sm mb-4">
          {anonymous ? 'This form is anonymous: your name and email are not collected.'
            : named ? 'Your name and email are required. If you send the form again, your earlier answers are replaced.'
            : 'Your name and email are optional.'}
        </p>

        <form onSubmit={submit}>
          {!anonymous && (
            <div className="grid grid-2">
              {block('name', (
                <div className="form-group">
                  <label>Name{named && ' *'}</label>
                  <input type="text" maxLength={255} autoComplete="name" value={identity.name}
                    onChange={e => setIdent('name', e.target.value)} />
                </div>
              ))}
              {block('email', (
                <div className="form-group">
                  <label>Email{named && ' *'}</label>
                  <input type="email" maxLength={255} autoComplete="email" inputMode="email" value={identity.email}
                    onChange={e => setIdent('email', e.target.value)} />
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-2">
            {block('site', <ListOrText label="Site Name/Location" options={form.site_options} value={identity.site}
              onChange={v => setIdent('site', v)} required={named} invalid={missing.has('site')} />)}
            {block('function', <ListOrText label="Function" options={form.function_options} value={identity.function}
              onChange={v => setIdent('function', v)} required={named} invalid={missing.has('function')} />)}
          </div>

          {form.template.sections.map(section => (
            <div key={section.title} className="mt-4">
              <h3 className="feedback-section">{section.title}</h3>
              {section.items.map((item, idx) => block(item.key, (
                <>
                  <div style={{ fontWeight: 500, marginBottom: '8px' }}>
                    {item.text}{item.required && ' *'}
                  </div>
                  {item.type === 'scale' && (
                    <Scale value={answers[item.key]} labels={form.scales[item.scale]} na={item.na} invalid={missing.has(item.key)}
                      onChange={v => {
                        setAnswer(item.key, v);
                        // A follow-up only stands while the answer that asked it does.
                        if (item.followUp && !item.followUp.when.includes(v)) {
                          setAnswers(a => { const n = { ...a }; delete n[item.followUp.key]; return n; });
                        }
                      }} />
                  )}
                  {item.followUp && item.followUp.when.includes(answers[item.key]) && (
                    <div className="mt-3">
                      <div style={{ fontWeight: 500, marginBottom: '8px' }}>{item.followUp.text} *</div>
                      <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                        {item.followUp.options.map(o => (
                          <button key={o} type="button" className="btn btn-sm"
                            onClick={() => setAnswer(item.followUp.key, o)}
                            style={answers[item.followUp.key] === o
                              ? { background: '#f97316', color: 'white' }
                              : { background: 'white', border: `1px solid ${missing.has(item.followUp.key) ? '#ef4444' : '#d1d5db'}`, color: '#374151' }}>
                            {o}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {item.type === 'choice' && (
                    <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                      {item.options.map(o => (
                        <button key={o} type="button" className="btn btn-sm"
                          onClick={() => setAnswer(item.key, o)}
                          style={answers[item.key] === o
                            ? { background: '#f97316', color: 'white' }
                            : { background: 'white', border: `1px solid ${missing.has(item.key) ? '#ef4444' : '#d1d5db'}`, color: '#374151' }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  )}
                  {item.type === 'text' && (
                    <textarea rows={3} maxLength={2000} value={answers[item.key] || ''}
                      onChange={e => setAnswer(item.key, e.target.value)} />
                  )}
                </>
              ), item.followUp && item.followUp.key))}
              {/* Per-trainer ratings follow the team questions. */}
              {section.title === 'Trainers' && form.trainer_names.length > 0 && (
                <>
                  <div className="text-muted text-sm" style={{ margin: '12px 0 4px' }}>Overall rating for each trainer</div>
                  {form.trainer_names.map(name => block(`trainer:${name}`, (
                    <>
                      <div style={{ fontWeight: 500, marginBottom: '8px' }}>{name} *</div>
                      <Scale value={trainerRatings[name]} labels={form.scales.quality} na
                        invalid={missing.has(`trainer:${name}`)}
                        onChange={v => {
                          setTrainerRatings(r => ({ ...r, [name]: v }));
                          setMissing(m => { const n = new Set(m); n.delete(`trainer:${name}`); return n; });
                        }} />
                      <textarea rows={2} maxLength={1000} className="mt-2"
                        placeholder="Comments, if any (optional)"
                        value={trainerComments[name] || ''}
                        onChange={e => setTrainerComments(c => ({ ...c, [name]: e.target.value }))} />
                    </>
                  )))}
                </>
              )}
            </div>
          ))}

          {error && <div className="alert alert-error mt-4">{error}</div>}
          <button type="submit" className="btn btn-primary btn-lg mt-4" style={{ width: '100%' }} disabled={submitting}>
            {submitting ? 'Sending...' : 'Submit feedback'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PublicFeedback;
